// Risk-language screen for the crisis / child-safety path (Vermont Act 156 work +
// child-safety). This runs BEFORE any Anthropic API call in every feature that
// sends the teacher's free text. On a hit, the caller must render
// <SafetyInterstitial /> and make NO model call and generate NO text.
//
// Authored as a plain ESM module (with a hand-written safety.d.ts for the app's
// TypeScript) so it is the single source of truth AND directly importable by the
// Node test suite (netlify/tests). Tune the keyword list here, in one place.
//
// Matching is case-insensitive and word-boundary aware, so "danger" matches
// "in danger" but not "dangerous", and short words never match inside longer
// unrelated words.
//
// Design choice: this errs toward routing to human help. A benign false positive
// (e.g. "cutting paper") shows the safety screen rather than an AI answer; the
// interstitial offers a Back action so the teacher can rephrase. That trade-off
// is deliberate for a child-safety gate.

// Tuned to catch genuine crisis / self-harm / abuse language while NOT firing on
// ordinary classroom behavior. Earlier bare words ("cutting", "hitting him/her",
// "hurt himself", "danger") produced false alarms — a kid cutting paper, hitting a
// classmate, getting hurt at recess, or being "in danger of falling behind" would
// wrongly route to the crisis screen. Those are now narrowed to their real-risk
// phrasings; the unambiguous terms (suicide, self-harm, abuse, neglect, unsafe at
// home) stay broad.
export const RISK_KEYWORDS = [
  // Suicide / wanting to die
  'suicide',
  'suicidal',
  'kill himself',
  'kill herself',
  'kill themselves',
  'kill myself',
  'wants to die',
  'want to die',
  'wanting to die',
  // Self-harm (kept broad — unambiguous)
  'self-harm',
  'self harm',
  'selfharm',
  // Cutting — narrowed to self-harm context (so "cutting paper / in line" is fine)
  'cutting himself',
  'cutting herself',
  'cutting themselves',
  'cuts himself',
  'cuts herself',
  'self-cutting',
  // Intent to hurt self — narrowed to intent (so "hurt himself at recess" is fine)
  'hurt himself on purpose',
  'hurt herself on purpose',
  'wants to hurt himself',
  'wants to hurt herself',
  'trying to hurt himself',
  'trying to hurt herself',
  // Abuse / neglect (kid-on-kid "hitting" no longer trips; real abuse still does)
  'abuse',
  'abused',
  'neglect',
  'unsafe at home',
  'hit at home',
  'hurt at home',
  'beaten at home',
  // Danger — narrowed to real-danger phrasing. Deliberately NOT the bare "in
  // danger" (it matches "in danger of falling behind"); requires who/what.
  'danger to himself',
  'danger to herself',
  'danger to others',
  'danger to themselves',
  'in immediate danger',
];

// One word-boundary, case-insensitive matcher per keyword. Spaces in a phrase are
// allowed to be any run of whitespace so "unsafe  at   home" still hits.
const RISK_PATTERNS = RISK_KEYWORDS.map((kw) => {
  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  return new RegExp(`\\b${escaped}\\b`, 'i');
});

// True if the teacher's text contains any risk language. Callers must route to
// the SafetyInterstitial and skip the API entirely when this returns true.
export function containsRiskLanguage(text) {
  if (!text) return false;
  const t = String(text);
  return RISK_PATTERNS.some((re) => re.test(t));
}
